import {
  ApiOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  RollbackOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Skeleton, Tag, Tooltip } from "antd";
import React from "react";
import { OmsPipelineCounts, OmsQueue, OmsStage } from "../oms.api";
import styles from "./pipeline_board.module.scss";

export type StageDef = {
  key: OmsStage;
  label: string;
  owner: string;
  hint: string;
  accent: string;
  tag: string;
};

export const FULFILMENT: StageDef[] = [
  {
    key: "to_source",
    label: "Check availability",
    owner: "Availability team",
    hint: "Some items don't have a supplier or warehouse confirmed yet.",
    accent: "#fa8c16",
    tag: "orange",
  },
  {
    key: "procuring",
    label: "Waiting for goods",
    owner: "Procurement team",
    hint: "PO is placed — waiting for the goods to reach our warehouse.",
    accent: "#d4b106",
    tag: "gold",
  },
  {
    key: "ready_to_dispatch",
    label: "Ready to ship",
    owner: "Warehouse team",
    hint: "Goods are with us — a truck still needs to leave.",
    accent: "#1677ff",
    tag: "blue",
  },
  {
    key: "in_transit",
    label: "On the way",
    owner: "Logistics team",
    hint: "The truck has left and is heading to the customer.",
    accent: "#2f54eb",
    tag: "geekblue",
  },
  {
    key: "service_to_confirm",
    label: "Installation due",
    owner: "Site audit / installation team",
    hint: "Goods delivered — only an installation or site visit is left to be marked done in CRM.",
    accent: "#eb2f96",
    tag: "magenta",
  },
];

export const AFTER_DELIVERY: StageDef[] = [
  {
    key: "delivered_not_billed",
    label: "Invoice missing",
    owner: "Billing team",
    hint: "Delivered, but no invoice has been created in Zoho yet.",
    accent: "#fa541c",
    tag: "volcano",
  },
  {
    key: "payment_unsettled",
    label: "Payment pending",
    owner: "Finance team",
    hint: "Invoice is created, but the payment isn't matched to it in Zoho yet.",
    accent: "#722ed1",
    tag: "purple",
  },
  {
    key: "delivered_open",
    label: "Return in progress",
    owner: "Ops team",
    hint: "Delivered and billed — a return or pickup is still open.",
    accent: "#13a8a8",
    tag: "cyan",
  },
];

export const HISTORY: StageDef[] = [
  {
    key: "completed",
    label: "Completed",
    owner: "",
    hint: "Delivered, billed and paid.",
    accent: "#52c41a",
    tag: "green",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    owner: "",
    hint: "Every item on the order was cancelled.",
    accent: "#8c8c8c",
    tag: "default",
  },
  {
    key: "anomaly",
    label: "Needs review",
    owner: "",
    hint: "Items are still on the order but every delivery was cancelled — nothing will reach the customer. Check these.",
    accent: "#cf1322",
    tag: "red",
  },
];

export const STAGE_BY_KEY = Object.fromEntries(
  [...FULFILMENT, ...AFTER_DELIVERY, ...HISTORY].map((s) => [s.key, s])
) as Record<OmsStage, StageDef>;

export const QUEUES: {
  key: OmsQueue;
  label: string;
  hint: string;
  accent: string;
  icon: React.ReactNode;
}[] = [
  {
    key: "blocking_errors",
    label: "Stuck",
    hint: "Blocked — a step that is still open has failed, so the order can't move until it's fixed. Work these first.",
    accent: "#cf1322",
    icon: <WarningOutlined />,
  },
  {
    key: "overdue",
    label: "Late",
    hint: "Still in progress and past the promised date.",
    accent: "#c41d7f",
    icon: <ClockCircleOutlined />,
  },
  {
    key: "on_hold",
    label: "Waiting",
    hint: "Waiting on the customer — payment before shipping or installation, or approval of a customised design.",
    accent: "#595959",
    icon: <PauseCircleOutlined />,
  },
  {
    key: "returns_open",
    label: "Returns",
    hint: "A return pickup is open.",
    accent: "#08979c",
    icon: <RollbackOutlined />,
  },
  {
    key: "sync_failures",
    label: "Needs fixing",
    hint: "Has an error, but nothing is held up — a Zoho record that didn't go through (PO, credit note, void…) or a leftover error on a finished step. Fix after Stuck.",
    accent: "#d46b08",
    icon: <ApiOutlined />,
  },
];

const QUEUE_BY_KEY = Object.fromEntries(QUEUES.map((q) => [q.key, q])) as Record<
  OmsQueue,
  (typeof QUEUES)[number]
>;

const num = new Intl.NumberFormat("en-IN");

const accentVars = (accent: string) =>
  ({ "--accent": accent, "--accent-bg": `${accent}14` }) as React.CSSProperties;

interface Props {
  counts: OmsPipelineCounts | null;
  stage: OmsStage | null;
  queue: OmsQueue | null;
  onStage: (stage: OmsStage | null) => void;
  onQueue: (queue: OmsQueue | null) => void;
  dimmed: boolean;
}

const PipelineBoard: React.FC<Props> = ({ counts, stage, queue, onStage, onQueue, dimmed }) => {
  const card = (s: StageDef, gap: boolean) => {
    const cell = counts?.stages[s.key];
    const active = stage === s.key;
    return (
      <Tooltip
        key={s.key}
        placement="bottom"
        mouseEnterDelay={0.6}
        overlayStyle={{ maxWidth: 240 }}
        title={
          <>
            {s.hint}
            <div style={{ opacity: 0.65, marginTop: 4 }}>{s.owner}</div>
          </>
        }
      >
        <button
          type="button"
          aria-pressed={active}
          className={`${styles.card} ${active ? styles.cardActive : ""} ${gap ? styles.groupGap : ""}`}
          style={accentVars(s.accent)}
          onClick={() => onStage(active ? null : s.key)}
        >
          <span className={styles.cardLabel}>{s.label}</span>
          {cell ? (
            <span className={styles.cardCount}>{num.format(cell.total)}</span>
          ) : (
            <Skeleton.Button active size="small" style={{ width: 44, height: 22, margin: "2px 0" }} />
          )}
        </button>
      </Tooltip>
    );
  };

  const groupTitle = (title: string, defs: StageDef[], gap: boolean) => {
    const total = counts ? defs.reduce((n, s) => n + counts.stages[s.key].total, 0) : null;
    return (
      <div
        className={`${styles.groupTitle} ${gap ? styles.groupGap : ""}`}
        style={{ gridColumn: `span ${defs.length}` }}
      >
        <span>{title}</span>
        {total != null ? <span>{num.format(total)}</span> : null}
      </div>
    );
  };

  const queueCount = (q: OmsQueue) =>
    !counts ? null : stage ? counts.stages[stage][q] : counts.queues[q];

  return (
    <div className={`${styles.board} ${dimmed ? styles.dimmed : ""}`}>
      <div
        className={styles.strip}
        style={{ "--cols": FULFILMENT.length + AFTER_DELIVERY.length } as React.CSSProperties}
      >
        {groupTitle("Before delivery", FULFILMENT, false)}
        {groupTitle("After delivery", AFTER_DELIVERY, true)}
        {FULFILMENT.map((s) => card(s, false))}
        {AFTER_DELIVERY.map((s, i) => card(s, i === 0))}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.history}>
          <button
            type="button"
            className={`${styles.historyItem} ${!stage ? styles.historyActive : ""}`}
            onClick={() => onStage(null)}
          >
            All orders<b>{counts ? num.format(counts.all) : "…"}</b>
          </button>
          {HISTORY.map((s) => {
            const total = counts?.stages[s.key].total;
            const alert = s.key === "anomaly" && !!total;
            return (
              <Tooltip key={s.key} title={s.hint}>
                <button
                  type="button"
                  aria-pressed={stage === s.key}
                  className={`${styles.historyItem} ${stage === s.key ? styles.historyActive : ""} ${
                    alert ? styles.historyAlert : ""
                  }`}
                  onClick={() => onStage(stage === s.key ? null : s.key)}
                >
                  {s.label}
                  <b>{total != null ? num.format(total) : "…"}</b>
                </button>
              </Tooltip>
            );
          })}
        </div>

        <span className={styles.divider} aria-hidden />

        <div className={styles.queues}>
          <span className={styles.sectionLabel}>
            {stage ? ` · ${STAGE_BY_KEY[stage].label}` : "Needs attention"}
          </span>
          {QUEUES.map((q) => {
            const n = queueCount(q.key);
            const active = queue === q.key;
            return (
              <Tooltip key={q.key} title={q.hint}>
                <button
                  type="button"
                  aria-pressed={active}
                  className={`${styles.queue} ${active ? styles.queueActive : ""} ${
                    !n && !active ? styles.queueMuted : ""
                  }`}
                  style={accentVars(q.accent)}
                  onClick={() => onQueue(active ? null : q.key)}
                >
                  {q.icon}
                  {q.label}
                  <span className={`${styles.queueCount} ${!n ? styles.queueCountZero : ""}`}>
                    {n == null ? "…" : num.format(n)}
                  </span>
                </button>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const FilterSummary: React.FC<{
  stage: OmsStage | null;
  queue: OmsQueue | null;
  search: string;
  count: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onStage: (stage: OmsStage | null) => void;
  onQueue: (queue: OmsQueue | null) => void;
  onClearSearch: () => void;
}> = ({ stage, queue, search, count, page, pageSize, loading, onStage, onQueue, onClearSearch }) => {
  const range =
    count === 0
      ? "No orders"
      : `${num.format((page - 1) * pageSize + 1)}–${num.format(Math.min(page * pageSize, count))} of ${num.format(count)} orders`;
  return (
    <div className={styles.summary}>
      <span style={{ fontWeight: 500, color: "#262626" }}>{loading ? "Loading…" : range}</span>
      {search ? (
        <Tag closable onClose={onClearSearch} color="processing">
          Search: “{search}” · all orders
        </Tag>
      ) : (
        <>
          {stage ? (
            <Tag closable onClose={() => onStage(null)} color={STAGE_BY_KEY[stage].tag}>
              {STAGE_BY_KEY[stage].label}
            </Tag>
          ) : null}
          {queue ? (
            <Tag
              closable
              onClose={() => onQueue(null)}
              icon={QUEUE_BY_KEY[queue].icon}
              style={{ color: QUEUE_BY_KEY[queue].accent, borderColor: QUEUE_BY_KEY[queue].accent }}
            >
              {QUEUE_BY_KEY[queue].label}
            </Tag>
          ) : null}
        </>
      )}
    </div>
  );
};

export const lateRowClass = styles.lateRow;

export default PipelineBoard;
